<template>
  <div class="animated fadeIn">
    <div class="container">
      <b-modal
        ref="modalUnsavedChanges"
        :title="$t('Webportal.Dialog.UnsavedChanges.Title')"
        :ok-title="$t('Webportal.Dialog.UnsavedChanges.Ok')"
        :cancel-title="$t('Webportal.Dialog.UnsavedChanges.Cancel')"
        no-close-on-esc
        no-close-on-backdrop
        hide-header-close
      >
        {{ $t("Webportal.Dialog.UnsavedChanges.Text") }}
      </b-modal>

      <b-modal
        ref="modalProcessingNote"
        :title="$t('Webportal.Actions.SurveyRevision.ProcessingNote.Title')"
        :ok-title="$t('Webportal.Dialog.Ok')"
        ok-only
      >
        <span v-html="$t('Webportal.Actions.SurveyRevision.ProcessingNote', { url: $router.resolve({name: 'Notification settings'}).href })"></span>
      </b-modal>

      <b-modal
        ref="modalSurveyRevisionCreation"
        :title="$t('Webportal.Dialog.SurveyRevisionCreation.Title')"
        :ok-title="$t('Webportal.Dialog.Ok')"
        ok-only
        @ok="onSurveyRevisionCreationOk"
      >
        <div v-html="$t('Webportal.Dialog.SurveyRevisionCreation.Text', {
          days: `<b>${surveyInstanceData?.SurveyInstance?.SurveyRevisionCreationLimitInDays ?? ''}</b>`
        })"></div>
      </b-modal>

      <form @submit.prevent :novalidate="true">
        <b-card class="mb-3" v-bind:class="{ spinner: isBusy }">
          <template v-slot:header>
            <h5>
              <template v-if="isEditMode">
                {{ $t("Webportal.Actions.SurveyRevision") }}
                <span v-if="surveyRevision">
                  #{{ surveyRevision.SurveyRevisionVisibleId }}
                </span>
                <small
                  v-if="surveyRevision"
                  class="float-end col-form-label-sm text-muted"
                >
                  {{ getRevisionCreatedText(surveyRevision) }}
                </small>
              </template>
              <template v-else>
                {{ $t("Webportal.Actions.SurveyRevision.Create") }}
              </template>
            </h5>

            <survey-revision-header v-if="isInitialized"
              class="pt-2"
              :surveyRevision="surveyRevision"
              :surveyInstance="surveyInstanceData ? surveyInstanceData.SurveyInstance : null"
              :statusSelectionVisible="canChangeStatus && isEditMode"
              :statusSelectionDisabled="isFormChanged"
              :statusHistoryVisible="isEditMode"
              :relatedSurveyRevisionsVisible="isEditMode"
              :reasonSelectionVisible="isReasonSelectionVisible"
              :reasonSelectionDisabled="isReasonSelectionDisabled"
              :reasons="surveyInstanceData?.SurveyInstance?.SurveyRevisionReasons"
              :resultAdjustmentOverviewVisible="isResultAdjustmentVisible"
              @statusChanged="statusChanged"
              v-model="surveyRevisionReasons"
            />
          </template>

          <div v-if="!surveyRevision">
            <span v-if="!isBusy">{{ $t("Webportal.NoDataFoundPlain") }}</span>
          </div>
          <div v-if="isInitialized && surveyRevision">
            <b-form-group
              v-if="isEditMode && (!isExplanationReadOnly || surveyRevision.Explanation)"
              :label="$t('Webportal.Actions.SurveyRevision.Explanation')"
              label-size="sm"
            >
              <b-form-textarea
                v-model="surveyRevision.Explanation"
                :placeholder="$t('Webportal.Actions.SurveyRevision.Explanation.Placeholder')"
                :readonly="isExplanationReadOnly"
                max-rows="6"
                class="app-ctrl"
              />
            </b-form-group>

            <b-list-group flush>
              <b-list-group-item class="ps-0 pe-0 app-ctrl">
                <b-button
                  block
                  squared
                  v-b-toggle="`collapse-general`"
                  variant="primary"
                  class="text-start w-100"
                >
                  <span class="d-flex justify-content-between align-items-center w-100">
                    <span>{{ $t("Webportal.Actions.SurveyRevision.General") }}</span>
                    <font-awesome-icon
                      size="lg"
                      :icon="[
                        'far',
                        isGeneralVisible == true
                          ? 'caret-circle-up'
                          : 'caret-circle-down',
                      ]"
                    />
                  </span>
                </b-button>

                <b-collapse
                  class="mt-2 app-ctrl"
                  v-model="isGeneralVisible"
                  :id="`collapse-general`"
                >
                  <b-row>
                    <b-col lg>
                      <b-form-group label-size="sm">
                        <template v-slot:label>
                          {{ $t('Webportal.Actions.SurveyRevision.Remarks') }}
                          <span v-if="surveyInstanceData"
                            :class="['flag-icon', `flag-icon-${getRegion(surveyInstanceData.SurveyInstance.ClientLocation.DefaultLocalization)}`]" />
                        </template>
                        <b-form-textarea
                          v-model="surveyRevision.Remarks"
                          :placeholder="
                            !isEditMode
                              ? $t('Webportal.Actions.SurveyRevision.Remarks.Placeholder')
                              : null
                          "
                          :readonly="isEditMode"
                          max-rows="6"
                          class="app-ctrl"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col v-if="isEditMode && needsTranslation" lg>
                      <b-form-group label-size="sm">
                        <template v-slot:label>
                          {{ $t('Webportal.Translated') }}
                          <span v-if="surveyInstanceData"
                            :class="['flag-icon', `flag-icon-${getRegion(surveyInstanceData.SurveyInstance.Survey.DefaultLocalization)}`]" />
                        </template>
                        <b-form-textarea
                          v-model="surveyRevision.RemarksTranslated"
                          :placeholder="$t('Webportal.Translated.Placeholder')"
                          :readonly="isReadOnly"
                          max-rows="6"
                          class="app-ctrl"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>

                  <file-uploader
                    ref="generalFileUploader"
                    :label="$te('Webportal.Actions.SurveyRevision.Attachments') ? $t('Webportal.Actions.SurveyRevision.Attachments') : null"
                    :description="$te('Webportal.Actions.SurveyRevision.Attachments.Description') ? $t('Webportal.Actions.SurveyRevision.Attachments.Description') : null"
                    :value="surveyRevision.Attachments"
                    :upload-url="attachmentUploadUrl"
                    :readonly="isReadOnly"
                    @is-busy-changed="isGeneralFileUploaderBusy = $event"
                  />

                  <survey-revision-comments
                    v-if="isEditMode"
                    v-model="surveyRevision.Comments"
                    :survey-revision-id="surveyRevision.SurveyRevisionId"
                    :allow-create="!isReadOnly"
                    :allow-edit="surveyRevision.CanEditComment"
                    :allow-delete="surveyRevision.CanEditComment"
                    @is-busy-changed="isGeneralCommentsBusy = $event"
                  />
                </b-collapse>
              </b-list-group-item>

              <b-list-group-item
                class="ps-0 pe-0 app-ctrl"
                v-for="(surveyRevisionEntry, index) of surveyRevision.Entries"
                :key="index"
              >
                <b-button
                  block
                  squared
                  v-b-toggle="`collapse-question-${index}`"
                  variant="primary"
                  class="text-start w-100"
                >
                  <span class="d-flex justify-content-between align-items-center w-100">
                    <span>
                      {{ $t("Webportal.Question") }}&nbsp;
                      <span class="small">
                        {{ getQuestionText(surveyRevisionEntry) }}
                      </span>
                    </span>
                    <font-awesome-icon
                      size="lg"
                      :icon="[
                        'far',
                        surveyRevisionEntry._visible == true
                          ? 'caret-circle-up'
                          : 'caret-circle-down',
                      ]"
                    />
                  </span>
                </b-button>
                <b-collapse
                  class="mt-2"
                  v-model="surveyRevisionEntry._visible"
                  :id="`collapse-question-${index}`"
                >
                  <b-row>
                    <b-col>
                      <b-form-group
                        :label="$t('Webportal.Question')"
                        label-size="sm"
                      >
                        <b-form-select
                          v-model="surveyRevisionEntry.SurveyQuestionId"
                          :options="questionOptions"
                          class="h-auto"
                          style="white-space: normal; word-wrap: break-word"
                          :disabled="isEditMode"
                          @input="questionInput(surveyRevisionEntry, $event)"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col cols="auto" v-if="isResultAdjustmentVisible">
                      <b-form-group
                        :label="$t('Webportal.Actions.SurveyRevision.ResultAdjustment')"
                        label-size="sm"
                      >
                        <b-form-select
                          v-model="surveyRevisionEntry.WasResultAdjusted"
                          :options="resultAdjustedOptions"
                          :disabled="isResultAdjustmentDisabled" />
                      </b-form-group>
                    </b-col>
                  </b-row>

                  <b-row>
                    <b-col lg>
                      <b-form-group label-size="sm">
                        <template v-slot:label>
                          {{ $t('Webportal.Actions.SurveyRevision.Remarks') }}
                          <span v-if="surveyInstanceData"
                            :class="['flag-icon', `flag-icon-${getRegion(surveyInstanceData.SurveyInstance.ClientLocation.DefaultLocalization)}`]" />
                        </template>
                        <b-form-textarea
                          v-model="surveyRevisionEntry.Remarks"
                          :placeholder="$t('Webportal.Actions.SurveyRevision.Remarks.Placeholder')"
                          :readonly="isEditMode"
                          max-rows="6"
                          class="app-ctrl"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col v-if="isEditMode && needsTranslation" lg>
                      <b-form-group label-size="sm">
                        <template v-slot:label>
                          {{ $t('Webportal.Translated') }}
                          <span v-if="surveyInstanceData"
                            :class="['flag-icon', `flag-icon-${getRegion(surveyInstanceData.SurveyInstance.Survey.DefaultLocalization)}`]" />
                        </template>
                        <b-form-textarea
                          v-model="surveyRevisionEntry.RemarksTranslated"
                          :placeholder="$t('Webportal.Translated.Placeholder')"
                          :readonly="isReadOnly"
                          max-rows="6"
                          class="app-ctrl"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>

                  <file-uploader
                    ref="entryFileUploader"
                    :label="$te('Webportal.Actions.SurveyRevision.Attachments') ? $t('Webportal.Actions.SurveyRevision.Attachments') : null"
                    :description="$te('Webportal.Actions.SurveyRevision.Attachments.Description') ? $t('Webportal.Actions.SurveyRevision.Attachments.Description') : null"
                    :value="surveyRevisionEntry.Attachments"
                    :upload-url="attachmentUploadUrl"
                    :upload-url-params="{
                      'survey-revision-entry-id':
                        surveyRevisionEntry.SurveyRevisionEntryId,
                    }"
                    :readonly="isReadOnly"
                    @is-busy-changed="
                      isBusyChanged(
                        surveyRevisionEntry,
                        '_isFileUploaderBusy',
                        $event
                      )
                    "
                  />

                  <survey-revision-comments
                    v-if="isEditMode"
                    v-model="surveyRevisionEntry.Comments"
                    :survey-revision-id="surveyRevision.SurveyRevisionId"
                    :survey-revision-entry-id="surveyRevisionEntry.SurveyRevisionEntryId"
                    :allow-create="!isReadOnly"
                    :allow-edit="surveyRevision.CanEditComment"
                    :allow-delete="surveyRevision.CanEditComment"
                    :templates="commentTemplates.filter(t => t.category == (surveyRevisionEntry.WasResultAdjusted ? 'ResultAdjusted' : 'ResultNotAdjusted'))"
                    :template-selection-visible="isResultAdjustmentVisible"
                    :template-selection-disabled="surveyRevisionEntry.WasResultAdjusted == null"
                    @is-busy-changed="
                      isBusyChanged(
                        surveyRevisionEntry,
                        '_isCommentsBusy',
                        $event
                      )
                    "
                  />

                  <b-button
                    variant="outline-danger"
                    size="sm"
                    class="float-end"
                    v-if="!isEditMode && index > 0"
                    @click="removeQuestion(surveyRevisionEntry)"
                  >
                    <font-awesome-icon icon="trash-alt" />
                    {{ $t("Webportal.Delete") }}
                  </b-button>
                </b-collapse>
              </b-list-group-item>
            </b-list-group>

            <b-button
              class="mt-3"
              variant="secondary"
              size="sm"
              v-if="
                !isEditMode &&
                (maximumQuestionCount == null ||
                  maximumQuestionCount > surveyRevision.Entries.length)
              "
              :disabled="!isFormValid"
              @click="addQuestion()"
            >
              <font-awesome-icon icon="plus" />
              {{ $t("Webportal.Actions.SurveyRevision.AddQuestion") }}
            </b-button>
          </div>

          <template v-slot:footer>
            <div class="float-end">
              <button
                type="button"
                @click="cancel"
                class="btn btn-secondary"
                :disabled="isBusy || isChildBusy"
              >
                <font-awesome-icon
                  :icon="['far', isEditMode ? 'arrow-left' : 'ban']"
                />
                {{ isEditMode ? $t("Webportal.Back") : $t("Webportal.Cancel") }}
              </button>
              <button
                v-if="!isReadOnly"
                type="button"
                @click="submit"
                class="btn btn-primary"
                :disabled="
                  !isFormChanged || !isFormValid || isBusy || isChildBusy
                "
              >
                <font-awesome-icon :icon="['far', 'dot-circle']" />
                {{ isEditMode ? $t("Webportal.Save") : $t("Webportal.Actions.SendRequest") }}
              </button>
            </div>
          </template>
        </b-card>
      </form>
    </div>
  </div>
</template>

<script>
import i18n from "@/i18n";
import EventBus from "@/services/EventBus.js";
import ActionService from "@/services/ActionService";
import AppConfig from '@/mixins/AppConfig-APP_TARGET';
import SurveyRevisionHeader from "./SurveyRevisionHeader";
import SurveyRevisionComments from "./SurveyRevisionComments";
import FileUploader from "@/components/FileUploader";
import SurveyRevisionMixin from "@/mixins/SurveyRevisionMixin";

import useVuelidate from '@vuelidate/core';
import { required, email, helpers } from '@vuelidate/validators';
import { useToast } from 'bootstrap-vue-next';

export default {
  name: "SurveyRevision",

  mixins: [ AppConfig, SurveyRevisionMixin ],

  props: {
    surveyInstanceId: null,
  },

  components: {
    SurveyRevisionHeader,
    SurveyRevisionComments,
    FileUploader,
  },

  setup () {
    const { show } = useToast();
    return { v$: useVuelidate(), showToast: show }
  },

  validations() {
    return {
      surveyRevision: {
        Explanation: { },
        Remarks: { required: helpers.withMessage(i18n.global.t('Webportal.Validation.Required'), required) },
        RemarksTranslated: { },
        Entries: {
          $each: {
            SurveyQuestionId: { required: helpers.withMessage(i18n.global.t('Webportal.Validation.Required'), required) },
            QuestionRemarks: { required: helpers.withMessage(i18n.global.t('Webportal.Validation.Required'), required) },
            RemarksTranslated: { },
            WasResultAdjusted: { }
          }
        }
      }
    };
  },

  data() {
    return {
      isGeneralVisible: (APP_TARGET !== "porschesar" && APP_TARGET !== "skoda") ? true : false,
      isGeneralFileUploaderBusy: false,
      isGeneralCommentsBusy: false,
      isBusy: false,

      surveyRevision: null,
      surveyInstanceData: null,

      isInitialized: false,
      shouldShowCreationModal: false,
      uniqueEntryId: 1
    };
  },

  computed: {
    isFormChanged() {
      return this.v$.$anyDirty;
    },

    isFormValid() {
      return !this.v$.$error && this.v$.$anyDirty;
    },

    commentTemplates() {
      const regex = /^Webportal\.Actions\.SurveyRevision\.CommentTemplate\.(?<category>\w+)\.(?<name>\w+)(?<!\.Title)$/;
      const locale = i18n.global.locale.value || i18n.global.locale;
      const messages = i18n.global.getLocaleMessage(locale);
      const keys = Object.keys(messages).filter(key => regex.test(key));
      // Failed in Vue 3
      // const keys = Object.keys(i18n.messages[i18n.global.locale]).filter(key => regex.test(key));

      return _.sortBy(
        keys.map(key => {
          const match = key.match(regex);
          const category = match?.groups?.category ?? '';
          const name = match?.groups?.name ?? '';

          return {
            id: key,
            name,
            category,
            title: this.$te(key + '.Title') ? this.$t(key + '.Title') : name,
            text: this.$t(key)
          }
        }), 'title');
      // return _.sortBy(
      //   keys.map(key => {
      //     const { category, name } = key.match(regex).groups;

      //     return {
      //       id: key,
      //       name,
      //       category,
      //       title: this.$te(key + '.Title') ? this.$t(key + '.Title') : name,
      //       text: this.$t(key)
      //     }
      //   }), 'title');
    },

    isReasonSelectionVisible() {
      return this.isEditMode &&
        this.surveyRevision?.IsReasonVisible == true;
    },

    isReasonSelectionDisabled() {
      return this.isEditMode &&
        this.surveyRevision?.CanEditReason != true;
    },

    resultAdjustedOptions() {
      return [
        { value: null, text: '' },
        { value: true, text: this.$t('Webportal.Actions.SurveyRevision.ResultAdjustment.True') },
        { value: false, text: this.$t('Webportal.Actions.SurveyRevision.ResultAdjustment.False') }
      ];
    },

    isResultAdjustmentVisible() {
      return this.isEditMode &&
        this.surveyRevision?.IsResultAdjustmentVisible == true;
    },

    isResultAdjustmentDisabled() {
      return this.isEditMode &&
        this.surveyRevision?.CanEditResultAdjustment != true;
    },

    isEditMode() {
      return this.$route.params.id !== undefined;
    },

    isReadOnly() {
      return !this.surveyRevision || this.surveyRevision.IsReadOnly;
    },

    isExplanationReadOnly() {
      return this.isReadOnly || !this.surveyRevision || this.surveyRevision.IsExplanationReadOnly;
    },

    canChangeStatus() {
      return this.surveyRevision && this.surveyRevision.CanChangeStatus;
    },

    needsTranslation() {
      return this.surveyInstanceData?.SurveyInstance.Survey.DefaultLocalization !=
        this.surveyInstanceData?.SurveyInstance.ClientLocation.DefaultLocalization;
    },

    questionOptions() {
      return _(this.surveyInstanceData?.Questions)
        .groupBy((q) => q.ClusterText)
        .map((g) => ({
          label: _.first(g).ClusterText,
          options: g.map((cq) => ({
            text: `${cq.QuestionNumber ?? ""} ${cq.QuestionText}`,
            value: cq.SurveyQuestionId,
          })),
        }))
        .value();
    },

    attachmentUploadUrl() {
      return this.surveyRevision?.SurveyRevisionId
        ? process.env.BASE_URL +
            `api/action/survey-revision/${this.surveyRevision.SurveyRevisionId}/attachment`
        : null;
    },

    isChildBusy() {
      return (
        this.isGeneralFileUploaderBusy ||
        this.isGeneralCommentsBusy ||
        this.surveyRevision?.Entries.some(
          (e) => e._isCommentsBusy || e._isFileUploaderBusy
        )
      );
    },

    maximumQuestionCount() {
      return (APP_TARGET === "porschesar") ? 1 : null;
    },

    surveyRevisionReasons: {
      get: function() {
        return this.surveyRevision ? this.surveyRevision.Reasons : null;
      },
      set: function(newValue) {
        this.surveyRevision = { ...(this.surveyRevision || {}), Reasons: newValue };
      }
    },

    surveyRevisionForSubmit() {
      if (!this.isEditMode)
        return { ...this.surveyRevision, StatusId: this.revisionInitialStatusId };
      else {
        // Geänderte Felder in surveyRevision
        const changedFields = [];
        if (this.v$.surveyRevision.Remarks.$dirty) changedFields.push('Remarks');
        if (this.v$.surveyRevision.RemarksTranslated?.$dirty) changedFields.push('RemarksTranslated');
        if (this.v$.surveyRevision.Explanation?.$dirty) changedFields.push('Explanation');
        if (this.v$.surveyRevision.Reasons?.$dirty) changedFields.push('Reasons');

        // Geänderte Felder in Entries
        const changedEntryFields = [];
        const changedEntryIds = [];
        this.v$.surveyRevision.Entries.$each.forEach((entryV, idx) => {
          // Prüfe alle Felder, die dich interessieren
          ['SurveyQuestionId', 'QuestionRemarks', 'RemarksTranslated', 'WasResultAdjusted', 'Remarks'].forEach(field => {
            if (entryV[field] && entryV[field].$dirty) {
              changedEntryFields.push({ idx, field });
              // SurveyRevisionEntryId ggf. mit aufnehmen
              if (this.surveyRevision.Entries[idx]?.SurveyRevisionEntryId)
                changedEntryIds.push(this.surveyRevision.Entries[idx].SurveyRevisionEntryId);
            }
          });
        });

        // Einzigartige Entry-IDs
        const uniqEntryIds = _.uniq(changedEntryIds);

        // Baue das Objekt für den Submit
        const result = _.pick(this.surveyRevision, [
          'SurveyRevisionId', 'SurveyInstanceId', 'StatusId',
          ...changedFields
        ]);

        // Füge geänderte Entry-Felder hinzu
        result.Entries = this.surveyRevision.Entries.map((entry, idx) => {
          // Nur Felder übernehmen, die dirty sind
          const dirtyFields = changedEntryFields.filter(e => e.idx === idx).map(e => e.field);
          if (dirtyFields.length === 0) return null;
          // Baue ein minimales Objekt mit nur den dirty-Feldern + ID
          const entryObj = {};
          if (entry.SurveyRevisionEntryId) entryObj.SurveyRevisionEntryId = entry.SurveyRevisionEntryId;
          dirtyFields.forEach(field => entryObj[field] = entry[field]);
          return entryObj;
        }).filter(Boolean); // Entferne null-Einträge

        return result;
      }
    },

  },

  async created() {
    this.initializeTheme();

    if (!this.isEditMode && !this.surveyInstanceId)
      this.$router.replace({ name: "notfound" });

    if (this.isEditMode) {
      await this.getSurveyRevision(this.$route.params.id);
      if (this.surveyRevision) {
        await this.getSurveyInstanceData(this.surveyRevision.SurveyInstanceId);
      }
    } else {
      await this.getSurveyInstanceData(this.surveyInstanceId);

      if (this.surveyInstanceData?.SurveyInstance) {
        if (!this.surveyInstanceData.SurveyInstance.CanCreateSurveyRevision) {
          // Zeige Modal statt msgBoxOk
          this.shouldShowCreationModal = true;
        } else {
          this.surveyRevision = {
            SurveyInstanceId: this.surveyInstanceData.SurveyInstance.SurveyInstanceId,
            ExternalSurveyInstanceId: this.surveyInstanceData.SurveyInstance.ExternalSurveyInstanceId,
            StatusId: this.SurveyRevisionStatus.New,
            Entries: [{ UniqueEntryId: this.uniqueEntryId++, _visible: true }],
          };
        }
      }

    }

    EventBus.$on("LocalizationChanged", this.localizationChanged);

    this.isInitialized = true;
  },

  mounted() {
    if (this.shouldShowCreationModal) {
      this.$refs.modalSurveyRevisionCreation.show();
    }
  },

  beforeUnmount() {
    EventBus.$off("LocalizationChanged", this.localizationChanged);
  },

  beforeRouteLeave(to, from, next) {
    this.unsavedChanges((result) => {
      if (result !== false) next();
    });
  },

  methods: {
    localizationChanged(localizationId) {
      this.getSurveyInstanceData(this.surveyRevision.SurveyInstanceId);
    },

    getRegion(localization) {
      if (!localization || !localization.includes("-"))
        return "";
      else
        return localization.split("-")[1].toLowerCase();
    },

    isBusyChanged(surveyRevisionEntry, property, value) {
      surveyRevisionEntry[property] = value;
    },

    addQuestion() {
      this.surveyRevision.Entries.forEach((e) => (e._visible = false));
      this.surveyRevision.Entries.push({ UniqueEntryId: this.uniqueEntryId++, _visible: true });
    },

    removeQuestion(surveyRevisionEntry) {
      this.surveyRevision.Entries.splice(
        this.surveyRevision.Entries.findIndex((e) => e == surveyRevisionEntry),
        1
      );
    },

    getQuestionText(surveyRevisionEntry) {
      let surveyQuestion = this.findQuestion(
        surveyRevisionEntry.SurveyQuestionId
      );

      return surveyQuestion || surveyRevisionEntry.QuestionText
        ? `${
            surveyQuestion?.QuestionNumber ??
            surveyRevisionEntry.QuestionNumber ??
            ""
          } ${surveyQuestion?.QuestionText ?? surveyRevisionEntry.QuestionText}`
        : null;
    },

    findQuestion(surveyQuestionId) {
      return this.surveyInstanceData?.Questions.find(
        (q) => q.SurveyQuestionId == surveyQuestionId
      );
    },

    questionInput(surveyRevisionEntry, value) {
      let question = this.findQuestion(value);
      surveyRevisionEntry.QuestionNumber = question?.QuestionNumber;
      surveyRevisionEntry.QuestionText = question?.QuestionText;
    },

    unsavedChanges(callback) {
      if (this.isFormChanged || this.isChildBusy) {
        let dialog = this.$refs.modalUnsavedChanges;

        dialog.$off();
        dialog.$on("ok", (_) => callback());
        dialog.$on("cancel", (_) => callback(false));
        dialog.show();
      } else callback();
    },

    statusChanged(statusId) {
      let currentStatus = this.surveyRevision.StatusId;
      this.surveyRevision.StatusId = statusId;

      // Submit and undo local status change on error
      this.submit().catch(() => {
        this.surveyRevision.StatusId = currentStatus;
      });
    },

    getSurveyInstanceData(surveyInstanceId) {
      this.isBusy = true;

      return ActionService.getSurveyInstanceData(surveyInstanceId)
        .then((response) => {
          this.surveyInstanceData = !_.isEmpty(response.data)
            ? response.data
            : null;
        })
        .finally((_) => {
          this.isBusy = false;
        });
    },

    getSurveyRevision(surveyRevisionId) {
      this.isBusy = true;

      return ActionService.getSurveyRevision(surveyRevisionId)
        .then((response) => {
          let surveyRevision = !_.isEmpty(response.data) ? response.data : null;

          if (!_.isEmpty(surveyRevision?.Entries))
            surveyRevision.Entries[0]._visible = true;
          this.surveyRevision = surveyRevision;

          this.v$.$reset();
        })
        .finally((_) => {
          this.isBusy = false;
        });
    },

    cancel() {
      this.$router.go(-1);
    },

    onSurveyRevisionCreationOk() {
      this.$router.go(-1);
    },

    async submit() {
      this.isBusy = true;

      try {
        let response = await ActionService.setSurveyRevision(
          this.surveyRevisionForSubmit
        );

        this.showToast({
          props: {
            title: this.$t("Webportal.SaveSuccess.Text", {
              entity: this.$t("Webportal.Actions.SurveyRevision"),
            }),
            variant: "success",
            autoHide: true,
            delay: 2000,
          }
        });

        if (!this.isEditMode) {
          this.$router.replace({
            name: this.$router.currentRoute.name,
            params: { id: response.data.surveyRevisionId },
          });

          // Update local IDs for new revision and entries
          this.surveyRevision.SurveyRevisionId = response.data.surveyRevisionId;
          this.surveyRevision.Entries.forEach((entry) => {
            const surveyRevisionEntryId = response.data.entries?.find((e) => e.uniqueEntryId == entry.UniqueEntryId)?.surveyRevisionEntryId;
            entry.SurveyRevisionEntryId = surveyRevisionEntryId;
          });

          // Wait for DOM update to have FileUploader components ready
          await this.$nextTick();

          if (this.surveyInstanceData.SurveyInstance.ShowSurveyRevisionProcessingNote)
            this.$refs.modalProcessingNote.show();

          // Reload revision, also because component is not reloaded on eventual navigation change
          await this.getSurveyRevision(response.data.surveyRevisionId);

          this.v$.$validate();

          // Upload all file attachments
          await this.$refs.generalFileUploader.uploadFiles();
          for (const fileUploader of this.$refs.entryFileUploader)
            await fileUploader.uploadFiles();
        } else {
          await this.getSurveyRevision(response.data.surveyRevisionId);
        }
      } catch (error) {
        console.error(error);

        this.showToast({
          props: {
            title: this.$t("Webportal.SaveFailed.Text", {
              entity: this.$t("Webportal.Actions.SurveyRevision"),
            }),
            variant: "danger",
            autoHide: true,
            delay: 10000,
            noCloseButton: true,
          }
        });

        throw error;
      } finally {
        this.isBusy = false;
      }
    }
  },
}
</script>
<style lang="scss" scoped>
@import "../../scss/bmw/_variables.scss";

.list-group-item {
  // border: none;
  background-color: transparent;
}

</style>
